import React, { useEffect, useRef } from "react";
import UpdatesBanner from "../UpdatesBanner/UpdatesBanner";
import { Container } from "@material-ui/core";
import { NavLink } from "react-router-dom";
import "./ForNetworkOperator.css";

// ── Scroll-entrance animation hook ──────────────────────────────────────────
function useFadeUp() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) { el.style.opacity = 1; return; }
    el.style.opacity = "0";
    el.style.transform = "translateY(14px)";
    el.style.transition = "opacity 300ms ease-out, transform 300ms ease-out";
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        el.style.opacity = "1";
        el.style.transform = "translateY(0)";
        obs.unobserve(el);
      }
    }, { threshold: 0.12 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

// ── Reusable section wrapper ─────────────────────────────────────────────────
function Section({ bg = "#ffffff", children, id }) {
  const ref = useFadeUp();
  return (
    <section id={id} style={{ backgroundColor: bg, padding: "96px 0" }} className="eno-section">
      <Container>
        <div ref={ref}>{children}</div>
      </Container>
    </section>
  );
}

// ── Eyebrow label ────────────────────────────────────────────────────────────
function Eyebrow({ children }) {
  return (
    <p style={{ fontSize: "13px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1.2px", color: "#5B6660", marginBottom: "16px", textAlign: "left" }}>
      {children}
    </p>
  );
}

// ── Section headline ─────────────────────────────────────────────────────────
function H2({ children, center, light }) {
  return (
    <h2 style={{ fontSize: "clamp(1.875rem, 3.5vw, 2.25rem)", fontWeight: 700, color: light ? "#ffffff" : "#1F2A24", lineHeight: 1.15, marginBottom: "20px", textAlign: center ? "center" : "left" }}>
      {children}
    </h2>
  );
}

// ── Card ─────────────────────────────────────────────────────────────────────
function Card({ children, accent, style = {} }) {
  return (
    <div style={{ background: "#ffffff", border: `1px solid #CBD6CE`, borderTop: `3px solid ${accent || "#2F5D45"}`, borderRadius: "8px", padding: "28px", height: "100%", boxShadow: "0 2px 8px rgba(0,0,0,0.05)", ...style }}>
      {children}
    </div>
  );
}

// ── Bullet list ──────────────────────────────────────────────────────────────
function BulletList({ items, color = "#2F5D45" }) {
  return (
    <ul style={{ paddingLeft: 0, listStyle: "none", margin: 0 }}>
      {items.map((item) => (
        <li key={item} style={{ display: "flex", alignItems: "flex-start", gap: "10px", fontSize: "18px", color: "#1F2A24", marginBottom: "10px", lineHeight: 1.6 }}>
          <span style={{ color, fontWeight: 700, flexShrink: 0, marginTop: "2px" }}>→</span>
          {item}
        </li>
      ))}
    </ul>
  );
}

// ── CTA Button ───────────────────────────────────────────────────────────────
function CTABtn({ href, primary, children }) {
  return (
    <a href={href} className={primary ? "eno-btn-primary" : "eno-btn-secondary"}>
      {children}
    </a>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════════
export default function ForNetworKOperators() {
  return (
    <>

      {/* ── 01 HERO ─────────────────────────────────────────────────────── */}
      <UpdatesBanner
        className="forManufactures si-hero"
        bannerTitle={[
          <>
            Build and Scale Your Own Ecosystem Network.
            <br />
            Become the Operator—Don't Just Participate.
          </>,
        ]}
        bannerbodycopy1={[
          "GBR Farming enables agrifood organisations to become Ecosystem Network Operators (ENOs)—building, operating, governing and monetizing their own trusted ecosystem networks across participants, partners, workflows, digital tools, data and services",
        ]}
        bannerbodycopy="OWN | OPERATE | GOVERN | MONETIZE"
       /*  href2="/si-registration" */
        
        buttonText2="Become an ENO"
        href2="/book-demo"
  
        buttonText7="Explore Platform"
        href7="/PlatformSoon"
      />


      {/* ── 02 THE SHIFT ────────────────────────────────────────────────── */}
      <Section bg="#E9F0EA">
          <Eyebrow>The Shift</Eyebrow>
          <H2>Agrifood Is Becoming an Ecosystem Business</H2>
          <p className="eno-body">
            Agrifood value is no longer created by one organisation alone. It is created through networks of participants—farmers, suppliers, service providers, manufacturers, distributors, brands, buyers and partners.
          </p>
          <p className="eno-body" style={{ marginBottom: "48px" }}>
            The opportunity is to move beyond managing your own business—and <strong>operate the ecosystem around it.</strong>
          </p>

        <div className="row g-3">
          {["Farmers","Suppliers","Service Providers","Manufacturers","Distributors","Brands","Buyers","Partners"].map((p) => (
            <div key={p} className="col-6 col-sm-4 col-md-3">
              <div style={{ background: "#ffffff", border: "1px solid #CBD6CE", borderRadius: "8px", padding: "16px 12px", textAlign: "center", fontSize: "17px", fontWeight: 600, color: "#1F2A24" }}>
                {p}
              </div>
            </div>
          ))}
        <div style={{ background: "#E9F0EA", border: "1px solid #CBD6CE", borderLeft: "4px solid #2F5D45", borderRadius: "8px", padding: "28px 32px" }}>
           {/*  <p className="eno-body" style={{ marginBottom: "12px" }}>
              Significant value exists across the network—but no single operating environment may connect and coordinate it effectively.
            </p> */}
            <p className="eno-body" style={{ marginBottom: "12px" }}>
              The shift is from managing individual businesses to orchestrating connected ecosystems because significant value exists across the network
            </p>
            <p style={{ fontSize: "19px", fontWeight: 700, color: "#2F5D45", margin: 0 }}>
            The businesses that own and operate the network will shape the next generation of agrifood
            </p> 
         </div>

        </div>

       {/*  <p className="eno-callout" style={{ marginTop: 48, textAlign: "left" }}>
          From operating a business → to operating an ecosystem.
        </p> */}
      </Section>


      {/* ── 03 WHY THIS MATTERS ─────────────────────────────────────────── */}
     {/*  <Section bg="#ffffff">
          <Eyebrow>Why This Matters</Eyebrow>
          <H2>The Biggest Opportunity Is No Longer Inside One Organisation</H2>
          <p className="eno-body">
            Agrifood businesses increasingly depend on networks of organisations, people, assets, services, data and markets. Yet these ecosystems are often:
          </p>

          <div className="row g-3 mb-5"> {[ "Fragmented across organisations", "Managed through disconnected systems", "Dependent on manual coordination", "Difficult to scale", "Difficult to govern", "Difficult to measure", "Difficult to monetize as one ecosystem", ].map((item) => ( <div key={item} className="col-md-6"> <div style={{ display: "flex", alignItems: "center", background: "#F3F4F6", border: "1px solid #E5E7EB", borderRadius: "8px", padding: "16px 20px", }} > <span style={{ fontSize: "18px", color: "#1F2A24", lineHeight: 1.5, }} > {item} </span> </div> </div> ))} </div>

          <div style={{ background: "#E9F0EA", border: "1px solid #CBD6CE", borderLeft: "4px solid #2F5D45", borderRadius: "8px", padding: "28px 32px" }}>
            <p className="eno-body" style={{ marginBottom: "12px" }}>
              Significant value exists across the network—but no single operating environment may connect and coordinate it effectively.
            </p>
           <p className="eno-body" style={{ marginBottom: "12px" }}>
              The opportunity is to turn fragmented relationships into an operating ecosystem.
            </p>
            <p style={{ fontSize: "19px", fontWeight: 700, color: "#2F5D45", margin: 0 }}>
              This is the shift: <em>From managing a business → to operating an ecosystem.</em>
            </p> 
          </div>
      </Section> */}


      {/* ── 04 THE PROBLEM ──────────────────────────────────────────── */}
      <Section bg="#ffffff">
          <Eyebrow>The Problem</Eyebrow>
          <H2>You Already Have an Ecosystem —But you cannot Operate Them as One.</H2>
          <p className="eno-body" style={{ marginBottom: "40px" }}>Your organisation may already have:</p>

          <div className="row g-3 mb-5">
            {[
              { icon: "", label: "Customers and partners" },
              { icon: "", label: "Suppliers and producers" },
              { icon: "", label: "Distribution networks" },
              { icon: "", label: "Physical assets" },
              { icon: "", label: "Industry relationships" },
              { icon: "", label: "Operational knowledge" },
              { icon: "", label: "Data" },
              { icon: "", label: "Digital systems" },
              { icon: "", label: "Market access" },
            ].map(({ icon, label }) => (
              <div key={label} className="col-6 col-md-4">
                <div style={{ display: "flex", alignItems: "center", gap: "12px", background: "#ffffff", border: "1px solid #CBD6CE", borderRadius: "8px", padding: "16px 20px" }}>
                  <span style={{ fontSize: "22px" }}>{icon}</span>
                  <span style={{ fontSize: "17px", color: "#1F2A24", fontWeight: 500, lineHeight: 1.4 }}>{label}</span>
                </div>
              </div>
            ))}
          </div>

          <div style={{ background: "#2F5D45", borderRadius: "10px", padding: "32px 36px" }}>
            <p style={{ fontSize: "19px", color: "#ffffff", lineHeight: 1.75, margin: 0 }}>
              But these relationships, resources and activities are often fragmented. You cannot bring them together into a{" "}
              <strong style={{ color: "#A8D5B5" }}>trusted ecosystem network that your organisation can operate.</strong>
            </p>
          </div>
      </Section>



       {/* ── 05 THE SOLUTION──────────────────────────── */}
      <Section bg="#E9F0EA">
          <Eyebrow>The Solution</Eyebrow>
          <H2>Ecosystem Infrastructure to Build, Orchestarte, Operate, Govern and Monetize your own ecosystem network.</H2>
          <p className="eno-body" style={{ marginBottom: "40px" }}>Your ecosystem network can bring together:</p>

        <div className="row g-4">
          {[
            { icon: "", title: "Participants", desc: "Farmers, suppliers, buyers, manufacturers, service providers, partners and other ecosystem participants." },
            { icon: "", title: "Digital Tools", desc: "Applications and digital capabilities that help participants perform their activities." },
            { icon: "", title: "Services", desc: "Operational, commercial, financial, logistics, advisory and ecosystem services." },
            { icon: "", title: "Workflows", desc: "Structured processes that coordinate activities across multiple organisations." },
            { icon: "", title: "Data & Intelligence", desc: "Shared ecosystem data, operational information and intelligence for better decisions." },
            { icon: "", title: "Assets", desc: "Physical and digital assets connected to ecosystem operations." },
          ].map(({ icon, title, desc }) => (
            <div key={title} className="col-md-6 col-lg-4">
              <Card>
                <span style={{ fontSize: "2rem", display: "block", marginBottom: "14px" }}>{icon}</span>
                <h3 className="eno-card-title">{title}</h3>
                <p className="eno-card-body">{desc}</p>
              </Card>
            </div>
          ))}
        </div>

        <p className="eno-callout" style={{ marginTop: 48, textAlign: "left" }}>All working together as one operating ecosystem.</p>
      </Section>


      {/* ── 05 WHO WE SERVE ─────────────────────────────────────────────── */}
      <Section bg="#ffffff">
          <Eyebrow>Who We Serve</Eyebrow>
          <H2>Built for Organisations Ready to Become Ecosystem Network Operators</H2>
          <p className="eno-body" style={{ marginBottom: "40px" }}>
            GBR Farming is designed for organisations that already have—or want to build—a network around a business or industry opportunity.
          </p>

        <div className="row g-4">
          {[
            { icon: "", title: "Agrifood Enterprises", desc: "Build and operate networks around your products, markets, supply chains and services." },
            { icon: "", title: "CBBOs/NGOs, FPO Federations & Cooperatives", desc: "Connect members, services, markets, assets and value chains into an operating ecosystem." },
            { icon: "", title: "Agribusiness & Service Organisations", desc: "Build networks that coordinate producers, service providers, buyers and partners." },
            { icon: "", title: "Manufacturers/Processors & Brands", desc: "Create supply, sourcing, contract manufacturing & farming  and ecosystem networks around production." },
            { icon: "", title: "Sustainability & Climate Organisations", desc: "Build networks connecting projects, participants, assets, funding and measurable impact." },
            { icon: "", title: "Technology & Infrastructure Partners", desc: "Participate in and extend ecosystem networks through technology, infrastructure and services." },
          ].map(({ icon, title, desc }) => (
            <div key={title} className="col-md-6 col-lg-4">
              <Card>
                <div style={{ fontSize: "2rem", marginBottom: "14px" }}>{icon}</div>
                <h3 className="eno-card-title">{title}</h3>
                <p className="eno-card-body">{desc}</p>
              </Card>
            </div>
          ))}
        </div>

        <p className="eno-callout" style={{ marginTop: 48, textAlign: "left" }}>
          If you have participants, relationships, assets, capabilities or a market opportunity—you can build an ecosystem around them.
        </p>
      </Section>


      {/* ── 06 BECOME THE OPERATOR ──────────────────────────────────────── */}
      <Section bg="#E9F0EA">
          <Eyebrow>Become an ENO</Eyebrow>
          <H2>What Does It Mean to Become an Ecosystem Network Operator-ENO?</H2>
          <p className="eno-body" style={{ marginBottom: "40px" }}>
            An <strong>Ecosystem Network Operator (ENO)</strong> is an organisation that builds and operates a network around a strategic business or industry opportunity.
          </p>

        <div className="row g-4 mb-5">
          {[
            { word: "OWN", desc: "Own your ecosystem model, network and operating environment.", icon: "🏛️" },
            { word: "OPERATE", desc: "Coordinate participants, services, workflows and ecosystem activities.", icon: "⚙️" },
            { word: "GOVERN", desc: "Define rules, access, roles, processes and standards.", icon: "📋" },
            { word: "MONETIZE", desc: "Create and capture value from ecosystem participation, services and transactions.", icon: "💰" },
          ].map(({ word, desc, icon }) => (
            <div key={word} className="col-md-6 col-lg-3">
              <Card accent="#2F5D45">
                <div style={{ fontSize: "2rem", marginBottom: "12px" }}>{icon}</div>
                <p style={{ fontSize: "14px", fontWeight: 800, letterSpacing: "2px", color: "#2F5D45", textTransform: "uppercase", marginBottom: "12px" }}>{word}</p>
                <p className="eno-card-body" style={{ margin: 0 }}>{desc}</p>
              </Card>
            </div>
          ))}
        </div>

        <p className="eno-callout" style={{ textAlign: "left" }}>
          Become the organisation that operates the ecosystem—not simply another participant in it.
        </p>
      </Section>


      {/* ── 07 FROM BUSINESS TO ECOSYSTEM ───────────────────────────────── */}
     {/*  <Section bg="#ffffff">
          <Eyebrow>From Business to Ecosystem</Eyebrow>
          <H2>Change What Your Organisation Can Operate</H2>

          <div className="row g-4 mb-5">
            <div className="col-md-6">
              <div style={{ background: "#F3F4F6", border: "1px solid #CBD6CE", borderRadius: "10px", padding: "32px" }}>
                <p style={{ fontSize: "13px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px", color: "#5B6660", marginBottom: "16px" }}>Traditional Business</p>
                <div className="eno-chain">
                  {["Organisation", "Products & Services", "Customers"].map((n, i, arr) => (
                    <React.Fragment key={n}>
                      <span className="eno-chain__node eno-chain__node--grey">{n}</span>
                      {i < arr.length - 1 && <span className="eno-chain__arrow">→</span>}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div style={{ background: "#E9F0EA", border: "1px solid #CBD6CE", borderRadius: "10px", padding: "32px" }}>
                <p style={{ fontSize: "13px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px", color: "#2F5D45", marginBottom: "16px" }}>Ecosystem Business</p>
                <div className="eno-chain eno-chain--wrap">
                  {["Organisation", "Ecosystem Network", "Participants + Partners + Assets + Data + Services", "Multiple Value Flows"].map((n, i, arr) => (
                    <React.Fragment key={n}>
                      <span className={`eno-chain__node ${i === 0 || i === arr.length - 1 ? "eno-chain__node--accent" : "eno-chain__node--green"}`}>{n}</span>
                      {i < arr.length - 1 && <span className="eno-chain__arrow eno-chain__arrow--green">→</span>}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div style={{ background: "#1F2A24", borderRadius: "10px", padding: "32px 36px" }}>
            <p style={{ fontSize: "19px", color: "#A8D5B5", fontWeight: 600, lineHeight: 1.7, margin: 0 }}>
              Your organisation becomes the <strong style={{ color: "#ffffff" }}>orchestrator of the network.</strong> Instead of operating only your own products and services, you can operate the environment in which multiple organisations create and exchange value.
            </p>
          </div>
      </Section> */}


      {/* ── 08 WHAT YOUR ECOSYSTEM CAN ENABLE ──────────────────────────── */}
      {/* <Section bg="#E9F0EA">
          <Eyebrow>Ecosystem Elements</Eyebrow>
          <H2>One Ecosystem. Many Ways to Create Value.</H2>
          <p className="eno-body" style={{ marginBottom: "40px" }}>Your ecosystem network can bring together:</p>

        <div className="row g-4">
          {[
            { icon: "👥", title: "Participants", desc: "Farmers, suppliers, buyers, manufacturers, service providers, partners and other ecosystem participants." },
            { icon: "📱", title: "Digital Tools", desc: "Applications and digital capabilities that help participants perform their activities." },
            { icon: "🔧", title: "Services", desc: "Operational, commercial, financial, logistics, advisory and ecosystem services." },
            { icon: "🔄", title: "Workflows", desc: "Structured processes that coordinate activities across multiple organisations." },
            { icon: "📊", title: "Data & Intelligence", desc: "Shared ecosystem data, operational information and intelligence for better decisions." },
            { icon: "🏗️", title: "Assets", desc: "Physical and digital assets connected to ecosystem operations." },
          ].map(({ icon, title, desc }) => (
            <div key={title} className="col-md-6 col-lg-4">
              <Card>
                <span style={{ fontSize: "2rem", display: "block", marginBottom: "14px" }}>{icon}</span>
                <h3 className="eno-card-title">{title}</h3>
                <p className="eno-card-body">{desc}</p>
              </Card>
            </div>
          ))}
        </div>

        <p className="eno-callout" style={{ marginTop: 48, textAlign: "left" }}>All working together as one operating ecosystem.</p>
      </Section> */}


      {/* ── 08 WENO JOURNEY ──────────────────────────── */}
      <Section bg="#ffffff">
          <Eyebrow>ENO Journey</Eyebrow>
          <H2>From Opportunity to Operating Ecosystem Networks</H2>
          <p className="eno-body" style={{ marginBottom: "40px" }}>This is the complete lifecycle after an organisation decides to become an ecosystem operator.</p>

        <div className="eno-journey">
          {[
            { n: "01", title: "Identify", desc: "Identify the ecosystem opportunity." },
            { n: "02", title: "Design", desc: "Define your ecosystem model and operating structure." },
            { n: "03", title: "Build", desc: "Create your ecosystem network." },
            { n: "04", title: "Operate", desc: "Onboard participants and operate ecosystem activities." },
            { n: "05", title: "Govern", desc: "Manage rules, access, workflows and ecosystem standards." },
            { n: "06", title: "Scale", desc: "Expand participants, services, micro-ecosystems and networks." },
            { n: "07", title: "Monetize", desc: "Create and capture value from the ecosystem." },
          ].map(({ n, title, desc }) => (
            <div key={n} className="eno-journey__step">
              <div className="eno-journey__num">{n}</div>
              <h3 className="eno-journey__title">{title}</h3>
              <p className="eno-journey__desc">{desc}</p>
            </div>
          ))}
        </div>
      </Section>


      {/* ── 09 ECOSYSTEM NETWORKS ───────────────────────────────────────── */}
      <Section bg="#E9F0EA" id="our-solution">
          <Eyebrow>Ecosystem Networks You Can Build</Eyebrow>
          <H2>Build an Ecosystem Around Your Specific Opportunity</H2>
          <p className="eno-body" style={{ marginBottom: "40px" }}>
            GBR enables organisations to create different types of ecosystem networks without starting from a blank technology environment.
          </p>

        <div className="row g-4">
          {[
            { code: "CMN", name: "Contract Manufacturing Network", flow: "Source → Manufacture → Quality → Pack → Move → Deliver", desc: "Connect Brands, Manufacturers, suppliers, quality teams, logistics partners and other participants around contract manufacturing." },
            { code: "CBON", name: "CBON Network", flow: "Demand → Connect → Source → Coordinate → Fulfil", desc: "Create a coordinated network connecting buyers, producers, suppliers, aggregators and other participants around structured buying and sourcing relationships." },
            { code: "ADN", name: "Agri Dealer Network", flow: "Manufacturer → Distributor → Dealer → Farmer → Services", desc: "Connect manufacturers, distributors, agri-input companies, dealers, retailers, farmers and service providers through an organised agricultural distribution ecosystem." },
            { code: "BEN", name: "Bee Economy Network", flow: "Bees → Pollination → Production → Processing → Products → Markets → ESG", desc: "Connect beekeepers, farmers, pollination providers, processors, brands, buyers, ESG partners and other participants across the Bee Economy." },
            { code: "FEN", name: "Farm Investment Ecosystem", flow: "Investment → Farm → Operations → Production → Market → Returns", desc: "Connect investors, farm operators, land/assets, agri-service providers, technology partners and market participants around farm investment opportunities." },
            { code: "ESG", name: "ESG & Sustainability Ecosystem", flow: "Projects → Participants → Actions → Measurement → Impact", desc: "Connect enterprises, ESG partners, project developers, communities, contributors, assets, measurement systems and sustainability initiatives." },
            { code: "PEN", name: "Pollination Ecosystem Network", flow: "Crop → Pollination Demand → Hive Deployment → Field Operations → Outcome", desc: "Coordinate beekeepers, commercial farmers, pollination service providers, crop managers and other participants around pollination requirements." },
            { code: "LCC", name: "Logistics & Cold Chain Network", flow: "Source → Store → Move → Monitor → Deliver", desc: "Connect producers, aggregators, warehouses, cold-chain operators, transport providers, buyers and other supply-chain participants." },
            { code: "FPO", name: "Farmer & FPO Ecosystem Network", flow: "Farmers → FPO → Services → Production → Aggregation → Market", desc: "Connect farmers, FPOs, federations, buyers, service providers, financial partners, input providers and markets." },
          ].map(({ code, name, flow, desc }) => (
            <div key={code} className="col-md-6 col-lg-4">
              <Card>
                <span style={{ display: "inline-block", background: "#E9F0EA", border: "1px solid #CBD6CE", borderRadius: "6px", padding: "4px 12px", fontSize: "13px", fontWeight: 800, color: "#2F5D45", letterSpacing: "0.8px", marginBottom: "14px" }}>{code}</span>
                <h3 className="eno-card-title">{name}</h3>
                <p style={{ fontSize: "13px", fontWeight: 600, color: "#2F5D45", fontFamily: "monospace", background: "#E9F0EA", borderRadius: "6px", padding: "8px 12px", marginBottom: "12px", lineHeight: 1.6, wordBreak: "break-word" }}>{flow}</p>
                <p className="eno-card-body">{desc}</p>
              </Card>
            </div>
          ))}
        </div>

        {/* Other networks */}
       {/*  <div style={{ marginTop: "48px", background: "#E9F0EA", border: "1px solid #CBD6CE", borderRadius: "10px", padding: "32px" }}>
          <h3 style={{ fontSize: "20px", fontWeight: 700, color: "#1F2A24", marginBottom: "20px" }}>Other Ecosystem Networks</h3>
          <div className="row g-2">
            {["Contract Farming Networks","Agri Input Ecosystem Networks","Food Processing Ecosystems","Rural Entrepreneurship Networks","Commodity & Produce Supply Networks","Agri Asset Networks","Farm Services Networks","B2B Agrifood Buyer Networks","Sustainability Project Networks","Distribution & Retail Ecosystems"].map((item) => (
              <div key={item} className="col-md-6 col-lg-4">
                <div style={{ display: "flex", alignItems: "center", gap: "10px", fontSize: "17px", color: "#1F2A24", lineHeight: 1.5 }}>
                  <span style={{ color: "#2F5D45", fontWeight: 700 }}>+</span>{item}
                </div>
              </div>
            ))}
          </div>
          <p className="eno-callout" style={{ marginTop: 28, marginBottom: 0, fontSize: "18px", textAlign: "left" }}>
            Your ecosystem does not need to fit a predefined model. <em>Define the opportunity. Configure the network. Operate the ecosystem.</em>
          </p>
        </div> */}

        <div style={{ marginTop: "48px" }}>
          <CTABtn href="/si-registration" primary>Start Building Your Ecosystem</CTABtn>
        </div>
      </Section>


      {/* ── 10 BUILD A NETWORK ──────────────────────────────────────────── */}
    {/*   <Section bg="#E9F0EA">
          <Eyebrow>Build a Network</Eyebrow>
          <H2>What Could You Operate?</H2>
          <p className="eno-body" style={{ marginBottom: "40px" }}>Your ecosystem can be built around:</p>

          <div className="row g-3 mb-5">
            {[
              { icon: "🛒", label: "Markets & Marketplaces" },
              { icon: "🔗", label: "Supply Chains" },
              { icon: "🔧", label: "Tools & Services" },
              { icon: "🏗️", label: "Assets & Sites" },
              { icon: "🤝", label: "Partners & Communities" },
              { icon: "🎯", label: "Strategic Workflows" },
            ].map(({ icon, label }) => (
              <div key={label} className="col-6 col-sm-4 col-md-2">
                <div style={{ background: "#ffffff", border: "1px solid #CBD6CE", borderRadius: "10px", padding: "24px 12px", textAlign: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
                  <div style={{ fontSize: "2rem", marginBottom: "8px" }}>{icon}</div>
                  <p style={{ fontSize: "16px", fontWeight: 700, color: "#1F2A24", margin: 0 }}>{label}</p>
                </div>
              </div>
            ))}
          </div>

          <p className="eno-body">You define the opportunity. GBR provides the infrastructure to turn that opportunity into an operating ecosystem network.</p>
          <div style={{ marginTop: "32px" }}>
            <CTABtn href="/si-registration" primary>Start Building Your Ecosystem</CTABtn>
          </div>
      </Section>
 */}

      {/* ── 11 HOW TO GET STARTED ───────────────────────────────────────── */}
     {/*  <Section bg="#ffffff">
          <Eyebrow>How to Get Started</Eyebrow>
          <H2>From Idea to Ecosystem Operator</H2>
          <p className="eno-body" style={{ marginBottom: "40px" }}>Getting started does not require building everything from scratch. GBR provides the underlying ecosystem infrastructure so your organisation can move from <strong>ecosystem idea to operating network quickly.</strong></p>

        <div className="eno-steps">
          {[
            { n: "01", title: "Identify", desc: "Define the business or industry opportunity you want to build around." },
            { n: "02", title: "Configure", desc: "Define your ecosystem participants, network structure, services and operating requirements." },
            { n: "03", title: "Build", desc: "Create your ecosystem network using GBR ecosystem infrastructure." },
            { n: "04", title: "Launch", desc: "Onboard participants, activate digital tools and begin operating your network." },
            { n: "05", title: "Scale", desc: "Expand participants, services, micro-ecosystems and additional networks." },
          ].map(({ n, title, desc }, i, arr) => (
            <React.Fragment key={n}>
              <div className="eno-step">
                <div className="eno-step__num">{n}</div>
                <h3 className="eno-step__title">{title}</h3>
                <p className="eno-step__desc">{desc}</p>
              </div>
              {i < arr.length - 1 && <div className="eno-step__connector">→</div>}
            </React.Fragment>
          ))}
        </div>

        <p className="eno-callout" style={{ marginTop: 48, textAlign: "left" }}>
          Start with one opportunity. Build the network around it. Grow from there.
        </p>
        <div style={{ marginTop: "24px" }}>
          <CTABtn href="/book-demo" primary>Become an ENO</CTABtn>
        </div>
      </Section> */}


      {/* ── 12 LAUNCH IN A WEEK ─────────────────────────────────────────── */}
     {/*  <Section bg="#E9F0EA">
          <Eyebrow>Launch Quickly</Eyebrow>
          <H2>Launch Your Ecosystem Network in a Week</H2>
          <p className="eno-body" style={{ marginBottom: "40px" }}>You do not need a long technology implementation cycle to begin.</p>

        <div className="row g-4">
          {[
            { step: "DEFINE", desc: "Your organisation and ecosystem opportunity.", icon: "🎯" },
            { step: "CONFIGURE", desc: "Your network structure and operating requirements.", icon: "⚙️" },
            { step: "ACTIVATE", desc: "Your ecosystem environment and digital capabilities.", icon: "🔌" },
            { step: "ONBOARD", desc: "Your initial ecosystem participants.", icon: "👥" },
            { step: "LAUNCH", desc: "Begin operating your ecosystem network.", icon: "🚀" },
          ].map(({ step, desc, icon }) => (
            <div key={step} className="col-6 col-md-4 col-lg-2-4">
              <div style={{ background: "#ffffff", border: "1px solid #CBD6CE", borderRadius: "10px", padding: "28px 20px", textAlign: "center", height: "100%" }}>
                <div style={{ fontSize: "2rem", marginBottom: "12px" }}>{icon}</div>
                <p style={{ fontSize: "13px", fontWeight: 800, letterSpacing: "1.5px", color: "#2F5D45", textTransform: "uppercase", marginBottom: "10px" }}>{step}</p>
                <p style={{ fontSize: "17px", color: "#5B6660", lineHeight: 1.55, margin: 0 }}>{desc}</p>
              </div>
            </div>
          ))}
        </div>

        <p className="eno-callout" style={{ marginTop: 48, textAlign: "left" }}>
          Start small. Launch quickly. Scale continuously.<br />
          <span style={{ fontWeight: 400, fontSize: "18px" }}>Your ecosystem journey starts with the first network—not the final destination.</span>
        </p>
      </Section>
 */}

      {/* ── 13 SCALE BEYOND ONE NETWORK ─────────────────────────────────── */}
     {/*  <Section bg="#ffffff">
          <Eyebrow>Scale</Eyebrow>
          <H2>One Organisation. Multiple Ecosystem Networks.</H2>
          <p className="eno-body" style={{ marginBottom: "40px" }}>
            Your organisation does not have to operate only one ecosystem. You can build multiple networks around different opportunities.
          </p>

          <div className="row g-3 mb-5">
            {[
              { icon: "🐝", name: "Bee Economy Network" },
              { icon: "🌾", name: "Farm Enterprise Network" },
              { icon: "🏭", name: "Contract Manufacturing Network" },
              { icon: "🚜", name: "AgroDealer Network" },
              { icon: "🌿", name: "Sustainability & Climate Network" },
            ].map(({ icon, name }) => (
              <div key={name} className="col-md-6 col-lg-4">
                <div style={{ display: "flex", alignItems: "center", gap: "14px", background: "#E9F0EA", border: "1px solid #CBD6CE", borderRadius: "8px", padding: "18px 22px" }}>
                  <span style={{ fontSize: "1.8rem" }}>{icon}</span>
                  <span style={{ fontSize: "17px", fontWeight: 600, color: "#1F2A24" }}>{name}</span>
                </div>
              </div>
            ))}
          </div>

          <div style={{ background: "#E9F0EA", borderRadius: "10px", padding: "28px 32px" }}>
            <p style={{ fontSize: "16px", fontWeight: 700, color: "#5B6660", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "14px" }}>Each network can have</p>
            <div className="eno-chain eno-chain--wrap">
              {["Participants","Services","Workflows","Data","Assets","Economics"].map((n, i, arr) => (
                <React.Fragment key={n}>
                  <span className="eno-chain__node eno-chain__node--green">{n}</span>
                  {i < arr.length - 1 && <span className="eno-chain__arrow eno-chain__arrow--green">→</span>}
                </React.Fragment>
              ))}
            </div>
          </div>

          <p className="eno-callout" style={{ marginTop: 40, textAlign: "left" }}>Build one network. Scale into a network of networks.</p>
      </Section> */}


      {/* ── 14 EXTEND TO LAST MILE ──────────────────────────────────────── */}
      <Section bg="#ffffff">
          <Eyebrow>Last Mile</Eyebrow>
          <H2>Connect Your Ecosystem to Micro-Ecosystems</H2>
          <p className="eno-body" style={{ marginBottom: "40px" }}>
            Your ecosystem network can extend beyond the enterprise level into the physical and operational environment where value is actually created.
          </p>

          <div className="row g-3 mb-5">
            {["Farms","Processing Facilities","Warehouses","Distribution Centres","Field Operations","Local Entrepreneur Networks"].map((item) => (
              <div key={item} className="col-md-6 col-lg-4">
                <div style={{ display: "flex", alignItems: "center", gap: "12px", background: "#ffffff", border: "1px solid #CBD6CE", borderRadius: "8px", padding: "18px 22px" }}>
                  <span style={{ color: "#2F5D45", fontWeight: 700, fontSize: "18px" }}>→</span>
                  <span style={{ fontSize: "17px", color: "#1F2A24", fontWeight: 500 }}>{item}</span>
                </div>
              </div>
            ))}
          </div>

          <div style={{ background: "#2F5D45", borderRadius: "10px", padding: "28px 32px" }}>
            <div className="eno-chain eno-chain--wrap">
              {["Enterprise","Ecosystem Network","Micro-Ecosystem","Field Operations"].map((n, i, arr) => (
                <React.Fragment key={n}>
                  <span className="eno-chain__node eno-chain__node--white">{n}</span>
                  {i < arr.length - 1 && <span className="eno-chain__arrow" style={{ color: "#A8D5B5" }}>→</span>}
                </React.Fragment>
              ))}
            </div>
            <p style={{ color: "#A8D5B5", fontSize: "17px", marginTop: "16px", marginBottom: 0 }}>
              Connect the strategic ecosystem with the people, assets and activities operating at the last mile.
            </p>
          </div>
      </Section>


      {/* ── 15 YOUR ECOSYSTEM AS MOAT ───────────────────────────────────── */}
     {/*  <Section bg="#ffffff">
          <Eyebrow>Competitive Moat</Eyebrow>
          <H2>Build an Ecosystem Others Cannot Easily Replicate</H2>
          <p className="eno-body" style={{ marginBottom: "40px" }}>Your competitive advantage can extend beyond your products or services. As your ecosystem grows, you build:</p>

        <div className="row g-4">
          {[
            { icon: "🤝", title: "Network Relationships", desc: "A connected community of participants, partners and customers." },
            { icon: "🧠", title: "Ecosystem Knowledge", desc: "Operational knowledge accumulated across participants, workflows and activities." },
            { icon: "📊", title: "Data & Intelligence", desc: "A growing ecosystem data foundation that improves visibility and decision-making." },
            { icon: "🔄", title: "Operating Workflows", desc: "Repeatable processes connecting multiple organisations." },
            { icon: "🔧", title: "Integrated Digital Tools & Services", desc: "More tools & services working together within the same ecosystem." },
            { icon: "👥", title: "Participant Engagement", desc: "An active network that becomes increasingly valuable as participation grows." },
          ].map(({ icon, title, desc }) => (
            <div key={title} className="col-md-6 col-lg-4">
              <Card>
                <span style={{ fontSize: "2rem", display: "block", marginBottom: "14px" }}>{icon}</span>
                <h3 className="eno-card-title">{title}</h3>
                <p className="eno-card-body">{desc}</p>
              </Card>
            </div>
          ))}
        </div>

        <p className="eno-callout" style={{ marginTop: 48, textAlign: "left" }}>
          Your ecosystem becomes an operating capability—not simply a technology asset.
        </p>
      </Section> */}


      {/* ── 16 MONETIZATION ENGINE ──────────────────────────────────────── */}
      <Section bg="#E9F0EA">
          <Eyebrow>Monetization</Eyebrow>
          <H2>Turn Ecosystem Networks into Revenue Platforms</H2>
          <p className="eno-body" style={{ marginBottom: "40px" }}>An ecosystem network can create multiple revenue opportunities for its operator.</p>

        <div className="row g-4">
          {[
            { icon: "", title: "Participant Services", desc: "Offer digital tools, services and capabilities to ecosystem participants." },
            { icon: "", title: "Network Services", desc: "Create services around sourcing, distribution, logistics, operations, coordination and market access." },
            { icon: "", title: "Ecosystem Transactions", desc: "Enable commercial interactions between participants." },
            { icon: "", title: "Premium Ecosystem Capabilities", desc: "Offer advanced services, intelligence, infrastructure and specialised capabilities." },
            { icon: "", title: "Micro-Ecosystem Services", desc: "Extend services into farms, facilities, field operations and local networks." },
            { icon: "", title: "Strategic Ecosystem Services", desc: "Build new commercial offerings around the unique capabilities and relationships created by the network." },
          ].map(({ icon, title, desc }) => (
            <div key={title} className="col-md-6 col-lg-4">
              <Card>
                <span style={{ fontSize: "2rem", display: "block", marginBottom: "14px" }}>{icon}</span>
                <h3 className="eno-card-title">{title}</h3>
                <p className="eno-card-body">{desc}</p>
              </Card>
            </div>
          ))}
        </div>

        <div style={{ background: "#2F5D45", borderRadius: "10px", padding: "28px 36px", marginTop: "48px" }}>
          <p style={{ fontSize: "19px", color: "#A8D5B5", lineHeight: 1.75, margin: "0 0 8px" }}>
            The ecosystem network is not only a channel for your business.
          </p>
          <p style={{ fontSize: "19px", fontWeight: 700, color: "#ffffff", margin: 0 }}>
            It can become a platform for creating, delivering and capturing multiple streams of ecosystem value.
          </p>
        </div>
      </Section>


      {/* ── 17 VALUE FOR THE ECOSYSTEM ──────────────────────────────────── */}
   {/*    <Section bg="#ffffff">
          <Eyebrow>Ecosystem Value</Eyebrow>
          <H2>Your Network Creates Value for Every Participant</H2>
          <p className="eno-body" style={{ marginBottom: "40px" }}>An effective ecosystem enables participants to move through a continuous value cycle:</p>

        <div className="eno-value-cycle" style={{ margin: "0" }}>
          {[
            { word: "CONNECT", desc: "Bring the right participants, assets, services and opportunities together.", icon: "🔗" },
            { word: "COLLABORATE", desc: "Enable participants to work together across shared workflows and objectives.", icon: "🤝" },
            { word: "CREATE", desc: "Combine capabilities, resources, knowledge and assets to create new value.", icon: "✨" },
            { word: "DELIVER", desc: "Coordinate the activities required to deliver products, services and outcomes.", icon: "🚀" },
            { word: "CAPTURE", desc: "Enable participants and the ecosystem operator to capture the value created.", icon: "💰" },
          ].map(({ word, desc, icon }, i, arr) => (
            <React.Fragment key={word}>
              <div className="eno-value-cycle__step">
                <div className="eno-value-cycle__icon">{icon}</div>
                <div>
                  <p className="eno-value-cycle__word">{word}</p>
                  <p className="eno-value-cycle__desc">{desc}</p>
                </div>
              </div>
              {i < arr.length - 1 && <div className="eno-value-cycle__arrow">↓</div>}
            </React.Fragment>
          ))}
        </div>

        <div style={{ marginTop: "48px" }}>
          <p style={{ fontSize: "19px", fontWeight: 700, color: "#2F5D45", marginBottom: "4px" }}>The ENO creates the environment.</p>
          <p style={{ fontSize: "19px", fontWeight: 700, color: "#1F2A24" }}>The ecosystem creates the value.</p>
        </div>
      </Section> */}


      {/* ── 18 ECOSYSTEM VALUE & BUSINESS IMPACT ────────────────────────── */}
      <Section bg="#1F2A24">
        <div style={{ marginBottom: "48px" }}>
          <p style={{ fontSize: "13px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1.2px", color: "#A8D5B5", marginBottom: "16px", textAlign: "left" }}>Business Impact</p>
          <H2 light>From Ecosystem Activity to Business Impact</H2>
          <p style={{ fontSize: "19px", color: "#A8D5B5", lineHeight: 1.7, textAlign: "left" }}>
            Operating an ecosystem network can create impact across multiple dimensions.
          </p>
        </div>

        <div className="row g-4">
          {[
            { icon: "", title: "Business Growth", desc: "Expand markets, participants, services and revenue opportunities." },
            { icon: "", title: "Operational Efficiency", desc: "Coordinate activities across organisations, workflows and assets." },
            { icon: "", title: "Participant Value", desc: "Give ecosystem participants better access to services, markets, tools and opportunities." },
            { icon: "", title: "Data & Intelligence", desc: "Create greater visibility across the ecosystem and improve decision-making." },
            { icon: "", title: "Asset Utilisation", desc: "Connect physical and digital assets to real ecosystem activities." },
            { icon: "", title: "New Business Models", desc: "Create new services and revenue streams around the ecosystem." },
            { icon: "", title: "Ecosystem Scale", desc: "Expand from one network to multiple networks and micro-ecosystems." },
          ].map(({ icon, title, desc }) => (
            <div key={title} className="col-md-6 col-lg-4">
              <div style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "10px", padding: "28px" }}>
                <span style={{ fontSize: "2rem", display: "block", marginBottom: "14px" }}>{icon}</span>
                <h3 style={{ fontSize: "20px", fontWeight: 700, color: "#ffffff", marginBottom: "10px" }}>{title}</h3>
                <p style={{ fontSize: "17px", color: "#A8D5B5", lineHeight: 1.6, margin: 0 }}>{desc}</p>
              </div>
            </div>
          ))}
        </div>

        <p style={{ fontSize: "19px", fontWeight: 700, color: "#A8D5B5", textAlign: "left", marginTop: "48px" }}>
          Build the ecosystem. Operate it. Create value through it. Grow with it.
        </p>
      </Section>


      {/* ── 19 WHY GBR ──────────────────────────────────────────────────── */}
      <Section bg="#E9F0EA">
          <Eyebrow>Why GBR</Eyebrow>
          <H2>We provide the infrastructure to build, operate and govern an ecosystem </H2>
          <p className="eno-body" style={{ marginBottom: "16px" }}>
            GBR is not another enterprise business application or software platform to Digitize the business. GBR provides <strong>Ecosystem Infrastructure</strong> for organisations that want to  become an Ecosystem Network Operator-ENO to :
          </p>
          <p style={{ fontSize: "19px", fontWeight: 700, color: "#2F5D45", marginBottom: "40px", letterSpacing: "0.5px" }}>
            BUILD → OPERATE → GOVERN → MONETIZE → SCALE & OWN the ecosystem network
          </p>
{/* 
          <div className="eno-ladder">
            {[
              { label: "Software", desc: "Digitise individual activities." },
              { label: "Platform", desc: "Connect applications, users and services." },
              { label: "Ecosystem Infrastructure", desc: "Enable organisations to build, operate and govern entire ecosystem networks.", highlight: true },
            ].map(({ label, desc, highlight }, i) => (
              <React.Fragment key={label}>
                <div className={`eno-ladder__rung ${highlight ? "eno-ladder__rung--highlight" : ""}`}>
                  <p className="eno-ladder__label">{label}</p>
                  <p className="eno-ladder__desc">{desc}</p>
                </div>
                {i < 2 && <div className="eno-ladder__arrow">↓</div>}
              </React.Fragment>
            ))}
          </div> */}

         {/*  <p className="eno-body" style={{ marginTop: "32px" }}>
            GBR provides the infrastructure layer that enables an organisation to become an <strong>Ecosystem Network Operator.</strong>
          </p> */}
          <div style={{ marginTop: "28px" }}>
            <CTABtn href="/PlatformSoon" primary>Explore the Infrastructure</CTABtn>
          </div>
      </Section>


      {/* ── 20 ENO JOURNEY ──────────────────────────────────────────────── */}
      {/* <Section bg="#ffffff">
          <Eyebrow>Your ENO Journey</Eyebrow>
          <H2>From Opportunity to Operating Ecosystem</H2>
          <p className="eno-body" style={{ marginBottom: "40px" }}>This is the complete lifecycle after an organisation decides to become an ecosystem operator.</p>

        <div className="eno-journey">
          {[
            { n: "01", title: "Identify", desc: "Identify the ecosystem opportunity." },
            { n: "02", title: "Design", desc: "Define your ecosystem model and operating structure." },
            { n: "03", title: "Build", desc: "Create your ecosystem network." },
            { n: "04", title: "Operate", desc: "Onboard participants and operate ecosystem activities." },
            { n: "05", title: "Govern", desc: "Manage rules, access, workflows and ecosystem standards." },
            { n: "06", title: "Scale", desc: "Expand participants, services, micro-ecosystems and networks." },
            { n: "07", title: "Monetize", desc: "Create and capture value from the ecosystem." },
          ].map(({ n, title, desc }) => (
            <div key={n} className="eno-journey__step">
              <div className="eno-journey__num">{n}</div>
              <h3 className="eno-journey__title">{title}</h3>
              <p className="eno-journey__desc">{desc}</p>
            </div>
          ))}
        </div>
      </Section> */}


      {/* ── 21 FINAL CTA ────────────────────────────────────────────────── */}
      <section style={{ background: "linear-gradient(135deg, #1F2A24 0%, #2F5D45 100%)", padding: "96px 0" }}>
        <Container>
          <div style={{ maxWidth: 760, margin: "0 auto", textAlign: "center" }}>
            <H2 center light>Your Ecosystem Is Already Around You.</H2>

            <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "10px", marginBottom: "32px" }}>
              {["Your partners.","Your participants.","Your customers.","Your assets.","Your data.","Your capabilities.","Your relationships."].map((item) => (
                <span key={item} style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "100px", padding: "8px 20px", fontSize: "17px", color: "#ffffff", fontWeight: 500 }}>
                  {item}
                </span>
              ))}
            </div>

            <p style={{ fontSize: "20px", color: "#A8D5B5", lineHeight: 1.7, marginBottom: "40px" }}>
              The next step is to connect them into an ecosystem you can operate.
            </p>

            <h3 style={{ fontSize: "clamp(1.5rem, 3vw, 2rem)", fontWeight: 800, color: "#ffffff", marginBottom: "36px" }}>
              Become an Ecosystem Network Operator.
            </h3>

            <div style={{ display: "flex", flexWrap: "wrap", gap: "16px", justifyContent: "center" }}>
              <CTABtn href="/book-demo" primary>Become an ENO</CTABtn>
              <CTABtn href="/PlatformSoon">Explore the Platform</CTABtn>
            </div>
          </div>
        </Container>
      </section>

    </>
  );
}
